import { RootState, ThunkDispatch } from "core-integration/src/store";
import { submitEntryImage } from "core-integration/src/store/entries/thunks";
import { selectIsMyEntry } from "core-integration/src/store/player/reducers";
import { closeCrestSheet, openCrestSheet } from "core-integration/src/store/ui";
import { EntryCrestData } from "core-integration/src/store/ui/uiSlice";
import { Button } from "plos/src/components/buttons/Button";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useAdobeContext } from "../../../contexts/AdobeContext";
import { useTrackingContext } from "../../../contexts/TrackingContext";
import { CrestPanelControl } from "../../crests/CrestPanelControl";
import { AdobeExpressLogo } from "../../crestSheets/AdobeExpressLogo";
import { ChevronRight } from "../../icons/Chevrons";
import ReportCrestButton from "../../ReportCrestButton";
import EntryInfoPanel from "../EntryInfoPanel";
import { crestWrapper } from "./CrestPanel.css";

interface PanelProps {
  entryCrestData: EntryCrestData;
}

const CrestPanel = ({ entryCrestData }: PanelProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch<ThunkDispatch>();
  const { enabled, embed } = useAdobeContext();
  const { fireClickTrackEvent } = useTrackingContext();

  const mine = useSelector((state: RootState) =>
    selectIsMyEntry(state, entryCrestData.id),
  );

  const hasCrest =
    entryCrestData.club_badge_src &&
    entryCrestData.club_badge_src !== "Pending";
  const hasPendingCrest = entryCrestData.club_badge_src === "Pending";
  const isGeneratingBadge = !hasCrest && !hasPendingCrest;

  const trackGenerateTeamBadge = () => {
    fireClickTrackEvent({
      event_component: "button click",
      event_category: "fantasy team badge",
      event_detail: "Generate Team Badge",
    });
  };

  const handleOpenCreateSheet = () => {
    if (isGeneratingBadge) {
      trackGenerateTeamBadge();
    }
    dispatch(openCrestSheet(entryCrestData));
  };

  const handleCreateCrest = () => {
    // If SDK not enabled do nothing
    if (!enabled) return;

    if (isGeneratingBadge) {
      trackGenerateTeamBadge();
    }

    dispatch(closeCrestSheet());
    embed.createImage({
      badgeHolder: Boolean(hasCrest),
      onImageCreate: async (img: Blob, prompt: string) => {
        await dispatch(submitEntryImage(entryCrestData.id, img, prompt));
      },
    });
  };

  const headerAction = mine && enabled && (
    <Button appearance="tonal" size="small" onPress={handleOpenCreateSheet}>
      {isGeneratingBadge
        ? t("generateTeamBadge", "Generate Team Badge")
        : t("editMyBadge", "Edit My Badge")}
      <ChevronRight height={10} width={10} />
    </Button>
  );

  return (
    <EntryInfoPanel
      title={t("teamBadge", "Team Badge")}
      headerAction={headerAction}
    >
      <div className={crestWrapper}>
        {/* Only the owner can generate a badge, and only when Adobe is enabled */}
        <CrestPanelControl
          entryCrestData={entryCrestData}
          onPress={mine && enabled ? handleCreateCrest : undefined}
        />
        {!hasPendingCrest && <AdobeExpressLogo variant={"panel"} />}
      </div>
      {!mine && <ReportCrestButton entryId={entryCrestData.id} />}
    </EntryInfoPanel>
  );
};

export default CrestPanel;
